import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Pages of last resort. They use no router, no query and no translations,
 * because whatever failed may be one of those. Students read Polish,
 * Ukrainian or Russian, so the few words are in all three.
 */
function Notice({ lines, action }: { lines: string[]; action: string }) {
  return (
    <div className="cl flex min-h-dvh items-center justify-center p-8 text-center">
      <div className="flex max-w-md flex-col items-center gap-3">
        {lines.map((line, i) => (
          <p key={line} className={i === 0 ? 'm-0 font-semibold' : 'm-0 text-class-ink-soft'}>
            {line}
          </p>
        ))}
        <button type="button" className="cl-link mt-2" onClick={() => window.location.reload()}>
          {action}
        </button>
      </div>
    </div>
  )
}

/** The session could not be checked; see sessionUnreachable in lib/auth.ts. */
export function SessionUnreachable() {
  return (
    <Notice
      lines={[
        'Nie udało się połączyć z serwerem.',
        'Не вдалося з’єднатися із сервером. · Не удалось подключиться к серверу.',
      ]}
      action="Spróbuj ponownie · Спробувати ще раз · Повторить"
    />
  )
}

/**
 * Around the whole app. Below it, the studio and the student panel have
 * their own boundaries per page and per exercise; this one catches the rest
 * (the public site, sign-in, an invitation), where an uncaught render error
 * used to leave a white page.
 */
export class RootBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error(error, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children
    return (
      <Notice
        lines={['Coś poszło nie tak.', 'Щось пішло не так. · Что-то пошло не так.']}
        action="Odśwież stronę · Оновити сторінку · Обновить страницу"
      />
    )
  }
}
